import Matter from 'matter-js'
import { MainProduct } from '../../types/index'
import { createChainBody, createPendant } from './index'
import { createNewConstraint } from './create-new-constraint'
import { getMainProductVariantById } from '..'

export const renderBracelet = (
  engine: Matter.Engine,
  render: Matter.Render,
  mainProduct: MainProduct,
  activeMainProductVariantId: string,
  activePendantProductVariants: string[]
) => {
  Matter.World.clear(engine.world, false)

  const mainProductVariant = getMainProductVariantById(mainProduct, activeMainProductVariantId)
  const builderData = mainProductVariant.metafields.builderData
  const length = builderData.numberOfChains
  const hasClasp = Boolean(builderData.clasp)
  const sprites = builderData.sprites
  const textures = mainProductVariant.textureUrls

  if (!length || !sprites.length || !textures.length) {
    return
  }

  const centerX = 400
  const centerY = 380
  const radius = 140

  const startAngle = (3 * Math.PI / 2) + (2 * Math.PI)
  const endAngle = 3 * Math.PI / 2

  const linkSpriteCount = hasClasp ? Math.max(sprites.length - 1, 0) : sprites.length
  const resolveSpriteIndex = (index: number) => {
    if (!hasClasp || linkSpriteCount === 0) {
      return index % sprites.length
    }

    if (index === 0) {
      return 0
    }

    const linkIndex = (index - 1) % linkSpriteCount
    return 1 + linkIndex
  }

  const chains = Array.from({ length }).map((_, index) => {
    const spriteIndex = resolveSpriteIndex(index)
    const sprite = sprites[spriteIndex]

    const angle = startAngle + (index / length) * (endAngle - startAngle)

    const x = centerX + radius * Math.cos(angle)
    const y = centerY + radius * Math.sin(angle)

    const width = sprite.width
    const height = sprite.height
    const textureIndex = spriteIndex < textures.length
      ? spriteIndex
      : spriteIndex % textures.length
    const texture = textures[textureIndex] ?? textures[0]
    const label = `chain/${spriteIndex}`

    return createChainBody(x, y, width, height, texture, label)
  })

  const pendantPositionStep = Math.ceil(chains.length / (activePendantProductVariants.length + 1))
  const pendantPositions = activePendantProductVariants.map((_, index) => {
    const chainIndex = (index + 1) * pendantPositionStep
    const angle = startAngle + (chainIndex / length) * (endAngle - startAngle)

    const x = centerX + radius * Math.cos(angle)
    const y = centerY + radius * Math.sin(angle)

    return { x, y }
  })

  const pendants = activePendantProductVariants.map((pendant, index) => {
    const position = pendantPositions[index]
    return createPendant(position.x, position.y, mainProduct.metafields.pendantProducts, pendant, mainProduct)
  })

  const chainsAndPendantsLength = chains.length + pendants.length
  const chainsAndPendants: Matter.Body[] = []

  const chainLength = chains.length

  for (let i = 0; i < chainLength; i++) {
    const isPendantPosition = (i + 1) % pendantPositionStep === 0

    if (isPendantPosition && pendants.length > 0) {
      chainsAndPendants.push(pendants.shift()!)
    }

    chainsAndPendants.push(chains.shift()!)
  }

  let constraints = Array.from({ length: chainsAndPendantsLength - 1 }).map((_, i) => {
    const bodyA = chainsAndPendants[i]
    const bodyB = chainsAndPendants[i + 1]

    return createNewConstraint(bodyA, bodyB, mainProduct, activeMainProductVariantId)
  })

  constraints = [
    ...constraints,
    createNewConstraint(
      chainsAndPendants[chainsAndPendants.length - 1],
      chainsAndPendants[0],
      mainProduct,
      activeMainProductVariantId
    )
  ]

  chainsAndPendants.forEach(b => (b.collisionFilter.group = -1))

  const Categories = {
    WRIST: 0x0001,
    CHAIN: 0x0002
  }

  const wrist = Matter.Bodies.circle(centerX, centerY, radius , {
    isStatic: true,
    collisionFilter: {
      category: Categories.WRIST,
      mask: Categories.CHAIN
    },
    render: { visible: false }
  })

  const necklace = [...chainsAndPendants, ...constraints, wrist]

  for (let i = 0; i < Math.floor(chainsAndPendants.length / 2); i++) {
    const group = Categories.CHAIN
    const mask = Categories.WRIST

    chainsAndPendants[i].collisionFilter.category = group
    chainsAndPendants[i].collisionFilter.mask = mask

    chainsAndPendants[length - 1 - i].collisionFilter.category = group
    chainsAndPendants[length - 1 - i].collisionFilter.mask = mask
  }

  chainsAndPendants[0].isStatic = true

  Matter.Composite.add(engine.world, necklace)
}
